import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import {
  CurrencyCenteredAmount,
  CurrencyIcon,
} from '@root/entities/currency/ui'
import { nativePriceAtom } from '@root/features/realtime/atoms'

import type { WithdrawReferralModel } from '../../atoms'

type Props = {
  model: WithdrawReferralModel
}

export const ReferralWithdrawAmount: React.FC<Props> = ({ model }) => {
  const [withdrawAmount, setWithdrawAmount] = useAtom(model.withdrawAmount)
  const [priceUsd] = useAtom(nativePriceAtom)

  const [currency] = useAtom(nativeNetworkCurrencyAtom)
  const [withdrawAmountAvailable] = useAtom(model.withdrawAmountAvailable)

  const amountUsdState = useMemo(
    () => AsyncStateN.success(priceUsd.multipliedBy(withdrawAmount)),
    [priceUsd, withdrawAmount],
  )

  const handleMaxClick = () => setWithdrawAmount(withdrawAmountAvailable)
  const handleChange = (value: string) => {
    setWithdrawAmount(BigNumber(value.replaceAll(',', '')))
  }

  return (
    <Root>
      <NetworkLabel>
        <CurrencyIcon size={24} currency={currency} />
        <span>{currency.ticker}</span>
      </NetworkLabel>
      <CurrencyAmount
        amountRaw={
          withdrawAmount.isGreaterThan(0) ? withdrawAmount.toString() : ''
        }
        amountUsdState={amountUsdState}
        currency={currency}
        validation={undefined}
        onMaxClick={handleMaxClick}
        isMax={
          withdrawAmount.isGreaterThanOrEqualTo(withdrawAmountAvailable) &&
          withdrawAmountAvailable.isGreaterThan(0)
        }
        onChange={handleChange}
        disabled={withdrawAmountAvailable.isEqualTo(0)}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  align-items: center;
`

const NetworkLabel = styled.div`
  background: var(--background-2);

  border-radius: 30px;
  display: grid;
  grid-template-columns: auto 1fr;
  padding: 2px 7px 2px 2px;
  gap: 5px;
  align-items: center;
  place-self: center;
  margin-block: 22px 18px;

  > span {
    color: var(--text-12);
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
  }
`

const CurrencyAmount = styled(CurrencyCenteredAmount)`
  display: grid;
  justify-items: start;
`
